import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { createContext, useRef, useContext, useState } from "react";
import { useFloating, autoUpdate, offset, flip, shift, arrow, useClick, useDismiss, useFocus, useRole, useInteractions, FloatingArrow } from "@floating-ui/react";
import assert from "assert";
import { ThemePortal } from "../ThemePortal/ThemePortal.mjs";
import { atoms } from "../../css/atoms/atoms.mjs";
import { Box } from "../Box/Box.mjs";
import { ButtonIcon } from "../ButtonIcon/ButtonIcon.mjs";
import { Text } from "../Text/Text.mjs";
import { popoverContent, popoverContentInner } from "./PopoverRenderer.css.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconClose } from "../icons/IconClose/IconClose.mjs";
import { Columns } from "../Columns/Columns.mjs";
import { Column } from "../Column/Column.mjs";
const ARROW_HEIGHT = 7;
const GAP = 2;
const StaticTooltipContext = createContext(false);
const borderRadius = "large";
const PopoverContent = ({
  children,
  title,
  onClose
}) => /* @__PURE__ */ jsx(Box, { display: "flex", position: "relative", transition: "fast", children: /* @__PURE__ */ jsx(
  Box,
  {
    boxShadow: "large",
    background: "neutral",
    borderRadius,
    className: popoverContent,
    children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", alignY: "top", children: [
      /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Box, { className: popoverContentInner, children: /* @__PURE__ */ jsxs(Box, { display: "flex", flexDirection: "column", alignItems: "flexStart", marginTop: "xxxsmall", children: [
        title && /* @__PURE__ */ jsx(Box, { display: "flex", marginBottom: "xsmall", justifyContent: "spaceBetween", children: /* @__PURE__ */ jsx(Text, { children: title }) }),
        typeof children === "function" ? children({ close: onClose }) : children
      ] }) }) }),
      /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
        ButtonIcon,
        {
          tone: "neutral",
          variant: "transparent",
          icon: /* @__PURE__ */ jsx(IconClose, {}),
          label: "Close Button",
          size: "large",
          id: "tone-1",
          onClick: (event) => {
            event.preventDefault();
            event.stopPropagation();
            onClose();
          }
        }
      ) }) })
    ] })
  }
) });
const validPlacements = ["top", "bottom"];
const PopoverRenderer = ({
  trigger,
  title,
  placement = "top",
  position = "absolute",
  children
}) => {
  assert(
    validPlacements.includes(placement),
    `The 'placement' prop must be one of the following: ${validPlacements.join(", ")}`
  );
  const arrowRef = useRef(null);
  const isStatic = useContext(StaticTooltipContext);
  const [controlledVisible, setControlledVisible] = useState(false);
  const showPopover = isStatic || controlledVisible;
  const floating = useFloating({
    open: showPopover,
    onOpenChange: setControlledVisible,
    placement,
    strategy: position,
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        layoutShift: false
      });
      return cleanup;
    },
    middleware: [
      offset(ARROW_HEIGHT + GAP),
      flip(),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  });
  const { refs, floatingStyles, context } = floating;
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const focus = useFocus(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, focus, role]);
  const onClose = () => setControlledVisible(false);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    trigger({
      triggerProps: {
        tabIndex: 1,
        ref: refs.setReference,
        ...getReferenceProps(),
        cursor: "pointer"
      }
    }),
    showPopover && /* @__PURE__ */ jsx(ThemePortal, { children: /* @__PURE__ */ jsxs(
      "div",
      {
        role: "tooltip",
        className: atoms({
          reset: "div",
          zIndex: "notification"
        }),
        ref: refs.setFloating,
        style: floatingStyles,
        ...getFloatingProps(),
        children: [
          /* @__PURE__ */ jsx(PopoverContent, { title, onClose, children }),
          /* @__PURE__ */ jsx(FloatingArrow, { ref: arrowRef, context })
        ]
      }
    ) })
  ] });
};
export {
  PopoverRenderer
};
